/**
 * Curent user reducer
 *
 * - Self modifying actions; change password/profile
 * - Logged out actions; reset password, accept invite
 * - Load current user's group user management accept
 *
 */

import conxtdOut from "apis/conxtdOut";

const initialState = {
  enterprisesCount: 0,
  sitesCount: 0,
  loading: null,
  totp: false,
  totp_type: null,
  recovery_codes: [],
  error: {},
  actions: {
    changePassword: {
      success: null,
      loading: null,
      error: {},
    },
    resetPassword: {
      success: null,
      loading: null,
      error: {},
    },
    changeProfile: {
      success: null,
      loading: null,
    },
    changeMFA: {
      success: null,
      loading: null,
      error: {},
    },
    // Authenticator app setup: secret/QR code issued before MFA is switched to type "A"
    mfaSetup: {
      success: null,
      loading: null,
      error: {},
      secret: null,
      totp_provisioning_uri: null,
    },
    acceptInvite: {
      success: null,
      loading: null,
      error: {},
    },
    appIdentityCheck: {
      success: null,
      loading: null,
      error: {},
    },
  },
  // The current user's access to the current enterprise
  access: {
    loading: null,
  },
  type: "default",
};

const user = (state = initialState, action) => {
  switch (action.type) {
    case "REQUEST_USER_ACCEPT_INVITE_START":
      return {
        ...state,
        actions: {
          ...state.actions,
          acceptInvite: {
            ...initialState.actions.acceptInvite,
            loading: true,
          },
        },
      };
    case "REQUEST_USER_ACCEPT_INVITE_SUCCESS":
      return {
        ...state,
        actions: {
          ...state.actions,
          acceptInvite: {
            ...initialState.actions.acceptInvite,
            loading: false,
          },
        },
      };
    case "REQUEST_USER_ACCEPT_INVITE_FAILED":
      return {
        ...state,
        actions: {
          ...state.actions,
          acceptInvite: {
            ...initialState.actions.acceptInvite,
            loading: false,
            error: action.error,
          },
        },
      };
    // App Login Check
    case "REQUEST_APP_IDENTITY_CHECK_START":
      return {
        ...state,
        actions: {
          ...state.actions,
          appIdentityCheck: {
            ...initialState.actions.appIdentityCheck,
            loading: true,
          },
        },
      };
    case "REQUEST_APP_IDENTITY_CHECK_SUCCESS":
      return {
        ...state,
        actions: {
          ...state.actions,
          appIdentityCheck: {
            ...initialState.actions.appIdentityCheck,
            loading: false,
          },
        },
      };
    case "REQUEST_APP_IDENTITY_CHECK_FAILED":
      return {
        ...state,
        actions: {
          ...state.actions,
          appIdentityCheck: {
            ...initialState.actions.appIdentityCheck,
            loading: false,
            error: action.error,
          },
        },
      };
    /** App login Check end */
    case "REQUEST_USER_CREATE_ACCOUNT_START":
      return {
        ...state,
        actions: {
          ...state.actions,
          acceptInvite: {
            ...initialState.actions.acceptInvite,
            loading: true,
          },
        },
      };
    case "REQUEST_USER_CREATE_ACCOUNT_FAILED":
      return {
        ...state,
        actions: {
          ...state.actions,
          acceptInvite: {
            ...initialState.actions.acceptInvite,
            loading: false,
            error: action.error,
          },
        },
      };
    case "REQUEST_USER_CREATE_ACCOUNT_SUCCESS":
      return {
        ...state,
        firstName: action.firstName,
        lastName: action.lastName,
        actions: {
          ...state.actions,
          acceptInvite: {
            ...initialState.actions.acceptInvite,
            loading: false,
            success: true,
          },
        },
      };
    case "LOAD_USER_MANAGEMENT_ACCESS_RESET":
      return {
        ...state,
        access: {
          loading: null,
        },
      };
    case "LOAD_USER_MANAGEMENT_ACCESS_START":
      return {
        ...state,
        access: {
          loading: true,
        },
      };
    case "LOAD_USER_MANAGEMENT_ACCESS_FAILED":
      return {
        ...state,
        access: {
          loading: false,
        },
      };
    case "LOAD_USER_MANAGEMENT_ACCESS_CANCEL":
      return {
        ...state,
        access: {
          loading: null,
        },
      };
    case "LOAD_USER_MANAGEMENT_ACCESS_SUCCESS":
      return {
        ...state,
        access: {
          loading: false,
          ...action.access,
        },
      };
    case "REQUEST_CHANGE_PROFILE_START":
      return {
        ...state,
        actions: {
          ...state.actions,
          changeProfile: {
            ...state.actions.changeProfile,
            loading: true,
          },
        },
      };
    case "REQUEST_CHANGE_PROFILE_SUCCESS":
      const user = {
        ...state,
        ...action.user,
        actions: {
          ...state.actions,
          changeProfile: {
            ...state.actions.changeProfile,
            loading: false,
            success: true,
          },
        },
      };
      conxtdOut.updateSession({
        ...user,
        // Reset actions
        actions: initialState.actions,
      });

      return user;
    case "REQUEST_CHANGE_PROFILE_FAILED":
      return {
        ...state,
        actions: {
          ...state.actions,
          changeProfile: {
            ...state.actions.changeProfile,
            loading: false,
            error: action.error,
          },
        },
      };
    case "REQUEST_CHANGE_PROFILE_NOTIFIED":
      return {
        ...state,
        actions: {
          ...state.actions,
          changeProfile: {
            ...state.actions.changeProfile,
            success: null,
          },
        },
      };
    // PROFILE CHANGE MFA
    case "REQUEST_CHANGE_MFA_START":
      return {
        ...state,
        actions: {
          ...state.actions,
          changeMFA: {
            ...state.actions.changeMFA,
            loading: true,
          },
        },
      };
    case "REQUEST_CHANGE_MFA_SUCCESS":
      const userMFA = {
        ...state,
        ...action.user,
        recovery_codes: (action.user && action.user.recovery_codes) || [], // recovery codes are returned if authenticator app setup is confirmed
        actions: {
          ...state.actions,
          changeMFA: {
            ...state.actions.changeMFA,
            loading: false,
            success: true,
          },
          // Setup secret/QR code is single-use; clear it once MFA change is confirmed
          mfaSetup: initialState.actions.mfaSetup,
        },
      };
      conxtdOut.updateSession({
        ...userMFA,
        // Recovery codes are password-equivalent and are only needed for the
        // one render that shows them to the user, so keep them out of the
        // session cookie - MFA_CLEAR_RECOVERY_CODES only clears redux state.
        recovery_codes: [],
        // Reset actions
        actions: initialState.actions,
      });

      return userMFA;
    case "REQUEST_CHANGE_MFA_FAILED":
      return {
        ...state,
        actions: {
          ...state.actions,
          changeMFA: {
            ...state.actions.changeMFA,
            loading: false,
            error: action.error,
          },
        },
      };
    case "REQUEST_CHANGE_MFA_NOTIFIED":
      return {
        ...state,
        actions: {
          ...state.actions,
          changeMFA: {
            ...state.actions.changeMFA,
            success: null,
            error: {},
          },
        },
      };
    case "MFA_CLEAR_RECOVERY_CODES":
      return {
        ...state,
        recovery_codes: [],
      }
    // AUTHENTICATOR APP SETUP (secret/QR code issued prior to enabling MFA type "A")
    case "REQUEST_MFA_SETUP_START":
      return {
        ...state,
        actions: {
          ...state.actions,
          mfaSetup: {
            ...initialState.actions.mfaSetup,
            loading: true,
          },
        },
      };
    case "REQUEST_MFA_SETUP_SUCCESS":
      return {
        ...state,
        actions: {
          ...state.actions,
          mfaSetup: {
            ...state.actions.mfaSetup,
            ...action.mfaSetup,
            loading: false,
            success: true,
          },
        },
      };
    case "REQUEST_MFA_SETUP_FAILED":
      return {
        ...state,
        actions: {
          ...state.actions,
          mfaSetup: {
            ...state.actions.mfaSetup,
            loading: false,
            success: false,
            error: action.error,
          },
        },
      };
    case "REQUEST_MFA_SETUP_NOTIFIED":
      return {
        ...state,
        actions: {
          ...state.actions,
          mfaSetup: initialState.actions.mfaSetup,
        },
      };
    // PROFILE CHANGE PASSWORD
    case "REQUEST_CHANGE_PASSWORD_START":
      return {
        ...state,
        actions: {
          ...state.actions,
          changePassword: {
            ...state.actions.changePassword,
            loading: true,
          },
        },
      };
    case "REQUEST_CHANGE_PASSWORD_SUCCESS":
      return {
        ...state,
        actions: {
          ...state.actions,
          changePassword: {
            ...state.actions.changePassword,
            success: true,
            loading: false,
          },
        },
      };
    case "REQUEST_CHANGE_PASSWORD_FAILED":
      return {
        ...state,
        actions: {
          ...state.actions,
          changePassword: {
            ...state.actions.changePassword,
            error: action.error,
            loading: false,
          },
        },
      };
    case "REQUEST_CHANGE_PASSWORD_NOTIFIED":
      return {
        ...state,
        actions: {
          ...state.actions,
          changePassword: {
            ...state.actions.changePassword,
            success: null,
            error: {},
          },
        },
      };

    // RESET PASSWORD SEND EMAIL
    case "REQUEST_USER_RESET_PASSWORD_START":
      return {
        ...state,
        actions: {
          ...state.actions,
          resetPassword: {
            ...state.actions.resetPassword,
            loading: true,
          },
        },
      };
    case "REQUEST_USER_RESET_PASSWORD_SUCCESS":
      return {
        ...state,
        actions: {
          ...state.actions,
          resetPassword: {
            ...state.actions.resetPassword,
            success: true,
            loading: false,
          },
        },
      };
    case "REQUEST_USER_RESET_PASSWORD_FAILED":
      return {
        ...state,
        actions: {
          ...state.actions,
          resetPassword: {
            ...state.actions.resetPassword,
            error: action.error,
            loading: false,
          },
        },
      };
    case "REQUEST_USER_RESET_PASSWORD_NOTIFIED":
      return {
        ...state,
        actions: {
          ...state.actions,
          resetPassword: {
            ...state.actions.resetPassword,
            success: null,
            error: {},
          },
        },
      };
    // RESET PASSWORD
    case "SEND_USER_RESET_PASSWORD_EMAIL_START":
      return {
        ...state,
        actions: {
          ...state.actions,
          resetPassword: {
            // Reset state
            ...initialState.actions.resetPassword,
            loading: true,
          },
        },
      };
    case "SEND_USER_RESET_PASSWORD_EMAIL_SUCCESS":
      return {
        ...state,
        actions: {
          ...state.actions,
          resetPassword: {
            ...state.actions.resetPassword,
            success: true,
            loading: false,
          },
        },
      };
    case "SEND_USER_RESET_PASSWORD_EMAIL_FAILED":
      return {
        ...state,
        actions: {
          ...state.actions,
          resetPassword: {
            ...state.actions.resetPassword,
            error: action.error,
            loading: false,
          },
        },
      };
    case "SEND_USER_RESET_PASSWORD_EMAIL_NOTIFIED":
      return {
        ...state,
        actions: {
          ...state.actions,
          resetPassword: {
            ...state.actions.resetPassword,
            success: null,
            error: {},
          },
        },
      };

    case "LOGIN_START":
      return {
        ...state,
        loading: true,
        error: {},
      };
    case "LOGIN_CANCEL":
      return {
        ...state,
        loading: false,
      };
    case "LOGIN_TOTP":
      return {
        ...state,
        loading: false,
        totp: action.data.totp,
        totp_type: action.data.totp_type,
      };
    case "LOGIN_TOTP_FAILED":
      return {
        ...state,
        loading: false,
        totp: false,
        totp_type: null,
      };
    case "LOGIN_TOTP_RESET":
      return {
        ...state,
        loading: false,
        totp: false,
        totp_type: null,
      };

    case "LOGIN_SUCCESS":
      if (window.location.hostname === "app.conxtd.com") {
        if (!window.OneSignal.__initAlreadyCalled) {
          window.InitNotifications(action.user.sessionId);
        } else {
          window.$(".onesignal-bell-container").show();
        }
      }

      return {
        ...state,
        ...action.user,
        // if we get the enterprises and sites arrays (empty or not), then we know we've got here via the user logging in and getting the details back from the login endpoint.
        // - in this case we can use those arrays to check how many enterprises and sites the user has access to.
        // if enterprises and sites are undefined, then we know that we've got here from restoring a previous login.
        // - in this case we're getting back the user details stored in the user cookie, including the enterprises and sites count. restore those values back into the state.
        enterprisesCount: (action.enterprises !== undefined) ? action.enterprises.length : (action.user && action.user.enterprisesCount),
        sitesCount: (action.sites !== undefined) ? action.sites.length : (action.user && action.user.sitesCount),
        loading: false,
        // Restore user type from localstorage
        type: localStorage.getItem("userType") || "default",
        error: {},
        totp: false,
      };
    case "LOGIN_FAILED":
      return {
        ...state,
        loading: false,
        error: action.error,
      };
    case "LOGOUT_REQUEST":
      if (window.location.hostname === "app.conxtd.com") {
        // Hide onesignal bell on logout
        window.$(".onesignal-bell-container").hide();
        // Remove all player ids etc?
        // localStorage.clear();
      }
      return initialState;
    default:
      return state;
  }
};

export default user;